import React, { useEffect, useRef, useState } from 'react'

interface ScrollableWithGradientsProps {
  children: React.ReactNode
  className?: string
}

const ScrollableWithGradients: React.FC<ScrollableWithGradientsProps> = ({
  children,
  className = 'w-full flex flex-row gap-6 overflow-auto'
}) => {
  const [showLeftGradient, setShowLeftGradient] = useState(false)
  const [showRightGradient, setShowRightGradient] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const checkScrollGradients = () => {
    if (!scrollRef.current) return

    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
    const hasLeftOverflow = scrollLeft > 1 // Small threshold for sub-pixel issues
    const hasRightOverflow = scrollLeft < scrollWidth - clientWidth - 1 // -1 for rounding

    setShowLeftGradient(hasLeftOverflow)
    setShowRightGradient(hasRightOverflow)
  }

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    const resizeObserver = new ResizeObserver(() => {
      checkScrollGradients()
    })

    resizeObserver.observe(scrollElement)
    checkScrollGradients() // Initial check

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    scrollElement.addEventListener('scroll', checkScrollGradients)
    window.addEventListener('resize', checkScrollGradients)

    return () => {
      scrollElement.removeEventListener('scroll', checkScrollGradients)
      window.removeEventListener('resize', checkScrollGradients)
    }
  }, [])

  return (
    <div className='relative'>
      <div ref={scrollRef} className={className}>
        {children}
      </div>
      {/* Left gradient overlay */}
      {showLeftGradient && (
        <div className='absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-50 to-transparent pointer-events-none z-10' />
      )}
      {/* Right gradient overlay */}
      {showRightGradient && (
        <div className='absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50 to-transparent pointer-events-none z-10' />
      )}
    </div>
  )
}

export default ScrollableWithGradients
